// N-07 password flow — shared by NewUser (create), UserEdit (reset) and the
// login forced-change screen. Client-side mirror of the API's aggressive
// policy in utils/passwordPolicy.js (the server enforces it on
// POST /SignIn/change-password; admin create/reset is enforced here).

export interface PasswordIdentity {
  email?: string;
  firstName?: string;
  lastName?: string;
}

const SPECIAL = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

// Live-checklist rules, in display order. The identity rule is built per
// form because it depends on whose password is being set.
export const buildPasswordRules = (identity: PasswordIdentity = {}) => {
  const fragments = [
    identity.firstName || '',
    identity.lastName || '',
    (identity.email || '').split('@')[0],
  ]
    .map(f => f.trim().toLowerCase())
    .filter(f => f.length >= 3);

  const rules: { label: string; test: (pw: string) => boolean }[] = [
    { label: 'At least 10 characters', test: pw => pw.length >= 10 },
    { label: 'One uppercase letter', test: pw => /[A-Z]/.test(pw) },
    { label: 'One lowercase letter', test: pw => /[a-z]/.test(pw) },
    { label: 'One number', test: pw => /[0-9]/.test(pw) },
    { label: 'One special character', test: pw => SPECIAL.test(pw) },
  ];
  if (fragments.length) {
    rules.push({
      label: 'Does not contain your name or email',
      test: pw => !fragments.some(f => pw.toLowerCase().includes(f)),
    });
  }
  return rules;
};

/** First failing rule's message, or null when the password passes. */
export const validatePasswordAggressive = (
  pw: string,
  identity: PasswordIdentity = {}
): string | null => {
  for (const rule of buildPasswordRules(identity)) {
    if (!rule.test(pw)) return `Password must satisfy: ${rule.label.toLowerCase()}`;
  }
  return null;
};

// Character pools for generation. Ambiguous glyphs (O/0, l/1/I) excluded so a
// password read over a shoulder or off paper types back correctly.
const POOL_UPPER = 'ABCDEFGHJKMNPQRSTUVWXYZ';
const POOL_LOWER = 'abcdefghjkmnpqrstuvwxyz';
const POOL_DIGIT = '23456789';
const POOL_SPECIAL = '!@#$%^&*-_=+?';
const POOL_ALL = POOL_UPPER + POOL_LOWER + POOL_DIGIT + POOL_SPECIAL;

const randomInt = (max: number): number => {
  // crypto-quality randomness; Math.random fallback only for ancient browsers
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c?.getRandomValues) {
    const buf = new Uint32Array(1);
    c.getRandomValues(buf);
    return buf[0] % max;
  }
  return Math.floor(Math.random() * max);
};

/**
 * 16-character password guaranteed to satisfy every class rule: one pick from
 * each required pool, the rest from the combined pool, then a Fisher-Yates
 * shuffle so the guaranteed picks don't sit at predictable positions.
 */
export const generateStrongPassword = (): string => {
  const chars = [
    POOL_UPPER[randomInt(POOL_UPPER.length)],
    POOL_LOWER[randomInt(POOL_LOWER.length)],
    POOL_DIGIT[randomInt(POOL_DIGIT.length)],
    POOL_SPECIAL[randomInt(POOL_SPECIAL.length)],
  ];
  while (chars.length < 16) {
    chars.push(POOL_ALL[randomInt(POOL_ALL.length)]);
  }
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};
